<script lang="ts">
  import { page } from '$app/stores';
  import CtaCard from '$components/cta-card.svelte';
  import Drawer from '$components/drawer.svelte';
  import SectionTitle from '$components/section-title.svelte';
  import StyledByYouDrawer from '$components/styled-by-you-drawer.svelte';
  import Icon from '$components/ui/icon/icon.svelte';
  import SliderButton from '$components/ui/slider-button.svelte';

  import { storyblokEditable } from '$lib/actions/storyblok-editable';
  import { AnalyticsEvents, analyticsEvent } from '$lib/analytics';
  import { t } from '$lib/i18n';
  import { buildSrcSet } from '$lib/image';
  import { defaultLang } from '$lib/lang';
  import { shopify } from '$lib/shopify';
  import { drawer } from '$lib/stores/drawer';
  import { cn } from '$lib/utils';
  import { getImageAttributes } from '$lib/utils/cms';
  import { getHash } from '$lib/utils/strings';
  import { getUUID } from '$lib/utils/uuid';
  import { getProductImages } from '$lib/yotpo';
  import type { StyledByYouSectionStoryblok } from '$types/bloks';
  import { createQuery } from '@tanstack/svelte-query';

  let containerWidth = 0;
  let screenSize = 0;
  let scrollX: HTMLDivElement;
  const SCROLL_BY = 300;
  const drawerId = 'styled-by-you' + getUUID();
  const routeID = $page.route.id;
  const handle = routeID?.includes('/products/') ? $page.params.handle : $page.params.path;

  export let block: StyledByYouSectionStoryblok;

  /* GraphQL */ `
    query getProductId($handle: String!) {
      product(handle: $handle) {
        id
        title
      }
    }
  `;

  $: reviews = handle
    ? createQuery({
        staleTime: 5 * 60 * 1000,
        queryKey: ['reviewsImages', handle],
        queryFn: async () => {
          const productData = await $shopify.getProductId({ handle });
          const productId = productData.product?.id || '';
          return await getProductImages({
            id: productId,
            page: 1,
            sort: 'date'
          });
        }
      })
    : null;

  $: reviewsImages = $reviews?.data ? $reviews.data.images : [];

  $: yotpoStyles = reviewsImages
    ?.filter((review) => review.media_type === 'image' && review.post?.created_time)
    .map((review) => {
      return {
        username: review.post?.username || '',
        image: review.original_image_url || '',
        products: review.tagged_products || [],
        date: review.post?.created_time || '',
        caption: review.post?.content || ''
      };
    });

  // TODO: Remove this block after migration
  const deprecatedStyles = block.styles?.map((style) => {
    return {
      username: style.username || '',
      image: style.image?.filename || '',
      products: style.products || [],
      date: style.date || '',
      caption: style.caption || ''
    };
  });

  const stylesList = block.styles_list?.map((item) => {
    if (typeof item === 'string') return;
    return item;
  });

  $: storyblokStyles =
    stylesList?.map((style) => {
      return {
        username: style?.content.username || '',
        image: style?.content.image?.filename || '',
        products: style?.content.products || [],
        date: style?.content.date || '',
        caption: style?.content.caption || ''
      };
    }) || deprecatedStyles;

  let maximum_to_show = block.maximum_to_show ? Number(block.maximum_to_show) : 8;
  $: isTruncated = defaultStyles.length > maximum_to_show;

  $: hasCta = block.cta && block.cta.length > 0;
  $: defaultStyles = [...(storyblokStyles ? storyblokStyles : []), ...yotpoStyles];
  $: allStyles = isTruncated ? [...defaultStyles].slice(0, maximum_to_show) : [...defaultStyles];
</script>

<svelte:window bind:innerWidth={screenSize} />
{#if !!allStyles.length}
  <section class="overflow-hidden pb-2 pt-[88px] lg:pb-8">
    <div
      class={cn(
        'flex w-full 2xl:container mx-auto justify-between items-center px-2 lg:mx-0 2xl:mx-auto lg:px-8 2xl:px-0 gap-4',
        block.title[0].variant === 'left' ? 'flex-row' : 'flex-col'
      )}
      use:storyblokEditable={block}
      bind:clientWidth={containerWidth}
    >
      <SectionTitle block={block.title[0]} />
      <!-- Slider Buttons-->
      <div
        class={cn(
          'hidden lg:flex mb-4 justify-end gap-2 px-2 lg:px-0',
          block.title[0].variant === 'left' ? 'w-fit' : 'w-full'
        )}
      >
        <SliderButton
          aria-label={$t('a11y.image-previous')}
          icon="chevron-left"
          variant="ghost"
          on:click={() => {
            scrollX?.scrollBy({ left: -SCROLL_BY, behavior: 'smooth' });
          }}
        />
        <SliderButton
          aria-label={$t('a11y.image-next')}
          icon="chevron-right"
          variant="ghost"
          on:click={() => {
            scrollX?.scrollBy({ left: SCROLL_BY, behavior: 'smooth' });
          }}
        />
      </div>
      <!-- END Slider Buttons -->
    </div>
    <div>
      {#if allStyles}
        <!-- Rendered Card -->
        <div>
          <div
            class="flex gap-2 overflow-auto hide-scrollbar lg:gap-4"
            style:--styled-margin={`${(screenSize - containerWidth) / 2 || 16}px`}
            bind:this={scrollX}
          >
            {#each allStyles as style, index}
              <button
                name={`styled-by-${style.username}`}
                on:click={() => {
                  analyticsEvent({
                    event: AnalyticsEvents.OPEN_STYLED_BY_YOU,
                    username: style.username || '',
                    date: style.date
                      ? new Date(style.date).toLocaleDateString(defaultLang, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })
                      : undefined
                  });
                  drawer.open(drawerId, {
                    meta: {
                      initialIndex: index
                    }
                  });
                }}
                class={cn(
                  index === 0 && 'ml-4 lg:ml-8 2xl:ml-[--styled-margin]',
                  index === allStyles.length - 1 &&
                    !hasCta &&
                    !isTruncated &&
                    'mr-4 lg:mr-8 2xl:mr-[--styled-margin]'
                )}
              >
                <div class="relative">
                  {#if style}
                    {#if style.image.includes('storyblok.com')}
                      {@const imageAsset = {
                        id: getHash(style.image),
                        filename: style.image,
                        alt: style.caption,
                        name: style.username,
                        focus: null,
                        source: null,
                        title: null,
                        copyright: null
                      }}
                      <img
                        loading="lazy"
                        class="aspect-square h-[240px] min-w-[240px] rounded-md object-cover"
                        src={getImageAttributes(imageAsset, { size: [400, 0] }).src}
                        alt={style.caption}
                        srcset={buildSrcSet({
                          loader: ({ width }) =>
                            getImageAttributes(imageAsset, { size: [width, 0] }).src,
                          widths: [400]
                        })}
                        sizes="400px"
                      />
                    {:else}
                      <img
                        src={style.image}
                        alt={style.caption}
                        class="aspect-square h-[240px] min-w-[240px] rounded-md object-cover"
                      />
                    {/if}
                  {/if}
                  <span class="absolute bottom-3 left-3 text-highlight">
                    <Icon icon="eye" size="sm" />
                  </span>
                </div>
              </button>
            {/each}
            {#if isTruncated && !hasCta}
              <div class="mr-4 lg:mr-8 2xl:mr-[--styled-margin]">
                <button
                  class={'relative flex bg-primary/4 rounded-sm items-center justify-center min-w-[240px] h-full'}
                  on:click={() => (isTruncated = !isTruncated)}
                >
                  <span class="w-fit text-primary text-xl">{$t('search.see-all')}</span>
                </button>
              </div>
            {/if}
            {#if hasCta && block.cta}
              <div class="mr-4 lg:mr-8 2xl:mr-[--styled-margin]">
                <CtaCard cta={block.cta[0]} class="min-w-[240px] h-full" />
              </div>
            {/if}
          </div>
        </div>
        <!-- Drawer -->
        <Drawer id={drawerId} title={$t('styled-by-you.title')}>
          <StyledByYouDrawer styles={allStyles} />
        </Drawer>
      {/if}

      {#if block.tag}
        <div class="container 2xl:mx-auto px-2 lg:px-8 2xl:px-0 w-full">
          <p class="ml-2 mt-4 text-primary/80">{block.tag}</p>
        </div>
      {/if}
    </div>
  </section>
{/if}
